import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { JiraPerson, ProductJiraField } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { inputClass } from '@/features/requests/request-form';
import { useJiraUsers, useProductJiraFields, useSaveProductJiraFields } from '@/hooks/use-admin';
import { ApiValidationError } from '@/lib/api';
import { cn } from '@/lib/utils';

type Draft = Record<string, { values: string[]; people: JiraPerson[] }>;

const isPeople = (field: ProductJiraField) => field.kind === 'user' || field.kind === 'users';
const isSingle = (field: ProductJiraField) => field.kind === 'user' || field.kind === 'text' || field.kind === 'option';

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-subtle py-0.5 pr-1 pl-2.5 text-xs">
      {label}
      <button type="button" aria-label={`Retirer ${label}`} onClick={onRemove} className="cursor-pointer border-0 bg-transparent p-0.5 text-muted-foreground">
        <X aria-hidden="true" className="size-3" />
      </button>
    </span>
  );
}

function PeopleInput({ field, value, onChange }: { field: ProductJiraField; value: JiraPerson[]; onChange: (people: JiraPerson[]) => void }) {
  const [q, setQ] = useState('');
  const users = useJiraUsers(q);
  const add = (person: JiraPerson) => {
    if (!value.some((p) => p.accountId === person.accountId)) onChange(isSingle(field) ? [person] : [...value, person]);
    setQ('');
  };
  const offered = [...(q.trim().length >= 2 ? (users.data ?? []) : field.suggestions.people)].filter((p) => !value.some((v) => v.accountId === p.accountId)).slice(0, 6);
  return (
    <>
      <input aria-label={`Chercher une personne pour ${field.name}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom ou adresse dans Jira" className={cn(inputClass, 'min-h-9 text-[13px]')} />
      {offered.length > 0 && (
        <span className="flex flex-wrap gap-1.5">
          {offered.map((p) => (
            <button key={p.accountId} type="button" onClick={() => add(p)} className="cursor-pointer rounded-full border border-dashed border-border bg-card px-2.5 py-0.5 font-[inherit] text-xs text-foreground-secondary">
              + {p.displayName}
            </button>
          ))}
        </span>
      )}
    </>
  );
}

function ValuesInput({ field, value, onChange }: { field: ProductJiraField; value: string[]; onChange: (values: string[]) => void }) {
  const [text, setText] = useState('');
  const add = (raw: string) => {
    // A Jira label cannot contain a space: the API refuses it, the input says it first.
    const v = field.kind === 'labels' ? raw.trim().replace(/\s+/g, '_') : raw.trim();
    if (v && !value.includes(v)) onChange(isSingle(field) ? [v] : [...value, v]);
    setText('');
  };
  const choices = field.options ?? field.suggestions.values;
  const listId = `jira-${field.id}-values`;
  if (field.options) {
    return (
      <select aria-label={`Ajouter une valeur à ${field.name}`} value="" onChange={(e) => add(e.target.value)} className={cn(inputClass, 'min-h-9 text-[13px]')}>
        <option value="">Choisir une valeur…</option>
        {field.options.filter((o) => !value.includes(o)).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  return (
    <span className="flex gap-2">
      <input
        aria-label={`Ajouter une valeur à ${field.name}`}
        list={listId}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add(text);
          }
        }}
        placeholder={field.kind === 'labels' ? 'Étiquette, sans espace' : 'Valeur'}
        className={cn(inputClass, 'min-h-9 min-w-0 flex-1 text-[13px]')}
      />
      <datalist id={listId}>
        {choices.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <Button type="button" variant="secondary" size="sm" disabled={!text.trim()} onClick={() => add(text)}>
        Ajouter
      </Button>
    </span>
  );
}

/**
 * Screen 12, ADR-013: values of the fields the product's Jira project requires
 * at creation (DP: PO, Team, Domaine, Produit, Lead Tech). Read from Jira at
 * each opening; the portal sends them with every ticket it creates.
 */
export function JiraFieldsCard({ productId }: { productId: string }) {
  const query = useProductJiraFields(productId);
  const save = useSaveProductJiraFields(productId);
  const [draft, setDraft] = useState<Draft>({});
  useEffect(() => {
    if (query.data) setDraft(Object.fromEntries(query.data.fields.map((f) => [f.id, { values: f.values, people: f.people }])));
  }, [query.data]);
  if (query.isPending) return <p className="m-0 text-[13px] text-muted-foreground">Lecture des champs obligatoires dans Jira…</p>;
  if (query.isError) return <p className="m-0 text-[13px] text-destructive">Les champs obligatoires de Jira n'ont pas pu être lus : {query.error.message}</p>;
  const { projectKey, fields } = query.data;
  if (!projectKey) return null;
  const missing = fields.filter((f) => f.kind !== 'unsupported' && (draft[f.id]?.values.length ?? 0) + (draft[f.id]?.people.length ?? 0) === 0);
  const error = save.error instanceof ApiValidationError ? save.error.issues.map((i) => i.message).join(' ') : save.error?.message;
  return (
    <form
      aria-labelledby="jira-fields-title"
      className="flex flex-col gap-3 rounded-card border border-border bg-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate({ fields: fields.filter((f) => f.kind !== 'unsupported').map((f) => ({ fieldId: f.id, values: draft[f.id]?.values ?? [], people: draft[f.id]?.people ?? [] })) });
      }}
    >
      <h2 id="jira-fields-title" className="m-0 text-base font-bold">
        Champs Jira obligatoires · {projectKey}
      </h2>
      <p className="m-0 text-xs text-muted-foreground">Valeurs envoyées avec chaque ticket créé depuis le portail pour ce produit. Les suggestions viennent des derniers tickets du projet.</p>
      {fields.length === 0 && <p className="m-0 text-[13px] text-muted-foreground">Le projet {projectKey} n'exige aucun champ sans valeur par défaut.</p>}
      {fields.map((field) => {
        const value = draft[field.id] ?? { values: [], people: [] };
        const update = (next: Partial<typeof value>) => setDraft((d) => ({ ...d, [field.id]: { ...value, ...next } }));
        return (
          <fieldset key={field.id} className="m-0 flex flex-col gap-1.5 border-0 p-0">
            <legend className="mb-1 p-0 text-[13px] font-bold">{field.name}</legend>
            {field.kind === 'unsupported' ? (
              <span className="text-xs text-destructive">Ce champ ne peut pas être rempli par le portail : rendez-le facultatif dans Jira.</span>
            ) : (
              <>
                {(value.values.length > 0 || value.people.length > 0) && (
                  <span className="flex flex-wrap gap-1.5">
                    {value.values.map((v) => (
                      <Chip key={v} label={v} onRemove={() => update({ values: value.values.filter((x) => x !== v) })} />
                    ))}
                    {value.people.map((p) => (
                      <Chip key={p.accountId} label={p.displayName} onRemove={() => update({ people: value.people.filter((x) => x.accountId !== p.accountId) })} />
                    ))}
                  </span>
                )}
                {isPeople(field) ? (
                  <PeopleInput field={field} value={value.people} onChange={(people) => update({ people })} />
                ) : (
                  <ValuesInput field={field} value={value.values} onChange={(values) => update({ values })} />
                )}
              </>
            )}
          </fieldset>
        );
      })}
      {missing.length > 0 && (
        <p className="m-0 rounded-control border border-warning-border bg-warning px-3 py-2 text-xs text-warning-foreground">
          Sans {missing.map((f) => `« ${f.name} »`).join(', ')}, Jira refuse la création d'un ticket pour ce produit.
        </p>
      )}
      {error && (
        <p role="alert" className="m-0 text-sm font-bold text-destructive">
          {error}
        </p>
      )}
      {save.isSuccess && !save.isPending && <p className="m-0 text-xs text-status-delivered-foreground">Champs enregistrés.</p>}
      {fields.length > 0 && (
        <Button type="submit" variant="secondary" className="self-end" disabled={save.isPending}>
          Enregistrer les champs Jira
        </Button>
      )}
    </form>
  );
}
